import * as React from 'react';
import {useDispatch} from 'react-redux';

import {colors} from 'shells/colors/colors';
import {TIMELINE_ENTRY_ICONS} from 'shells/timeline/gutter/consts';
import type {Lead} from 'shells/timeline/types';

import type {EventFragmentSparse, SessionUser} from 'nutshell-graphql-types';

// @ts-expect-error
import {appHistory} from '../../../history';

import {openReplyModal} from '../../email/email-reply-actions';
import {useGetSuggestedReplies} from '../../email/graphql/hooks/use-get-suggested-replies';

import {EmailMutationWrapper} from '../comments/graphql/email-mutation-wrapper';
import {useCreateComment, useDeleteComment} from '../comments/timeline-comment-mutations';
import {EmailEventActionMenu} from '../email-event-action-menu';
import {useGetRelatedLeads} from '../graphql/hooks/use-get-related-leads';
import {
    getEntryTypeFromPayload,
    getEmailIconVariant,
    getParticipantsForEmail,
    getActorForEmail,
    getStatusForEmail,
    getSubjectForEmail,
    getAdditionalToolbarOptionsForEmail,
    shouldEmailAppearOnLeadTimeline,
} from '../helpers';
import {EmailRelatedLeadsButton} from '../toolbar/email-related-leads-button';
import {useUpdateEmailLeadAssociations} from '../toolbar/use-update-email-lead-associations';

import {TimelineEmailProvider} from './timeline-email-context';
import {TimelineEntry} from './timeline-entry';

type OwnProps = {
    event: EventFragmentSparse;
    currentUser: SessionUser;
    onDeleteEvent?: (eventId: string, allById?: boolean) => Promise<any>;
    hasGutterLine?: boolean;
    isDashboard?: boolean;
    refetchEvents?: () => any | null | undefined;
    entityPageId?: string | null | undefined;
    onFilterOutEvent?: () => void;
    allowSuggestedReplies: boolean;
};

type Props = OwnProps & {
    onRetryFailedEmail: (emailId: string) => any;
    onUpdateEmail: (emailId: string, shouldShare: boolean) => any;
    onDeleteEmail: (emailId: string, allByAddress?: string | null | undefined) => any;
    eventEmailId: string;
};

export function TimelineEntryEmailComponent(props: Props) {
    const {id, changeType, changeTime, payload, reactions, comments} = props.event;
    const {
        currentUser,
        onRetryFailedEmail,
        onUpdateEmail,
        onDeleteEmail,
        onDeleteEvent,
        hasGutterLine,
        isDashboard,
        refetchEvents,
        entityPageId,
        onFilterOutEvent,
        allowSuggestedReplies,
        eventEmailId,
    } = props;

    const [isViewingAttachments, setIsViewingAttachments] = React.useState<boolean>(false);
    const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
    const [shouldShowSuggestedReplies, setShouldShowSuggestedReplies] =
        React.useState<boolean>(false);

    const emailEntryRef = React.useRef<HTMLDivElement | null>(null);

    const createComment = useCreateComment();
    const deleteComment = useDeleteComment();
    const dispatch = useDispatch();
    const {emailRelatedLeads} = useGetRelatedLeads({changeLogId: id});
    const updateEmailRelatedLeadAssociations = useUpdateEmailLeadAssociations(id);
    const {suggestedEmailReplies, isLoading: isLoadingSuggestedReplies} = useGetSuggestedReplies(
        eventEmailId,
        !shouldShowSuggestedReplies ||
            !NutClientConfig.enableAiEmailReplies ||
            !NutClientConfig.hasAiEmailReplies ||
            isDashboard
    );

    const onOpenReplyModal = (input: {
        replyToEmailId: string;
        isReplyingAll: boolean;
        onReplySuccess?: () => void;
        suggestedEmailReplyId?: string;
    }) => {
        dispatch(openReplyModal({...input}));
    };

    // @ts-expect-error
    const getCustomBorder = (email) => {
        const border: Record<string, any> = {};
        if (!email.isShared) {
            border.hasDottedBorder = true;
        }
        if (email.isFailed) {
            border.borderColor = colors.red;
        }

        return border;
    };

    const deleteEmailAndEvent = (emailId: string, allByAddress?: string | null) => {
        if (onDeleteEmail) {
            // If removing all by address, we don't want the optimistic removal of
            // the timeline entry because we need the popover to stay open to prompt
            // the user to blocklist the email
            if (allByAddress) {
                return onDeleteEmail(emailId, allByAddress);
            }

            // Note: this is the changeLogId, not the emailId
            // We need this to get the effects of removing a timeline entry
            // e.g. optimistic removal of the timeline entry
            // @ts-expect-error
            return onDeleteEvent(id);
        }
    };

    React.useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (allowSuggestedReplies) setShouldShowSuggestedReplies(true);
                }
            });
        });

        if (emailEntryRef.current) {
            observer.observe(emailEntryRef.current);
        }

        return () => observer.disconnect();
    }, [allowSuggestedReplies]);

    /**
     * We need to include this check, even though we are only passing an event
     * with a payload.__typename of 'Email' because Apollo doesn't generate a specific
     * enough type when using a union type (like the TimelineEventPayload).
     * See this issue for more detail:
     * https://github.com/apollographql/apollo-tooling/issues/1223
     */
    if (payload.__typename === 'Email') {
        const disableComments = !payload.isShared;

        const onReply = (suggestedEmailReplyId?: string) => {
            // Sometimes click events are passed through as the first argument
            // so we need to make sure we are a number, otherwise set it to
            // undefined
            let suggestedEmailReplyIdValue = suggestedEmailReplyId;
            if (typeof suggestedEmailReplyIdValue !== 'string') {
                suggestedEmailReplyIdValue = undefined;
            }

            const replyVariables = refetchEvents
                ? {
                      replyToEmailId: payload.id,
                      isReplyingAll: false,
                      onReplySuccess: refetchEvents,
                      suggestedEmailReplyId: suggestedEmailReplyIdValue,
                  }
                : {
                      replyToEmailId: payload.id,
                      isReplyingAll: false,
                      suggestedEmailReplyId: suggestedEmailReplyIdValue,
                  };

            return onOpenReplyModal(replyVariables);
        };

        const onReplyAll = () => {
            const replyVariables = refetchEvents
                ? {replyToEmailId: payload.id, isReplyingAll: true, onReplySuccess: refetchEvents}
                : {replyToEmailId: payload.id, isReplyingAll: true};

            return onOpenReplyModal(replyVariables);
        };

        const participants = getParticipantsForEmail(
            payload.recipients,
            emailRelatedLeads || undefined
        );
        const relatedLeads: Lead[] =
            emailRelatedLeads && emailRelatedLeads.edges
                ? emailRelatedLeads.edges.map((edge) => ({
                      type: 'leads',
                      id: edge.node.id,
                      name: edge.node.name,
                      avatarUrl: edge.node.avatarUrl,
                      number: edge.node.number,
                      status: edge.node.status,
                      priority: edge.node.priority,
                      htmlUrl: edge.node.htmlUrl,
                      isChecked: edge.isMapped,
                      relatedPerson: edge.node.contacts.primaryEdge
                          ? {
                                type: 'contacts',
                                id: edge.node.contacts.primaryEdge.node.id,
                                name: edge.node.contacts.primaryEdge.node.name,
                                htmlUrl: edge.node.contacts.primaryEdge.node.htmlUrl,
                            }
                          : undefined,
                      relatedCompany: edge.node.accounts.primaryEdge
                          ? {
                                type: 'accounts',
                                id: edge.node.accounts.primaryEdge.node.id,
                                name: edge.node.accounts.primaryEdge.node.name,
                                htmlUrl: edge.node.accounts.primaryEdge.node.htmlUrl,
                            }
                          : undefined,
                      value: edge.node.value ? {formatted: edge.node.value.formatted} : undefined,
                      assignee: edge.node.assignee
                          ? {id: edge.node.assignee.id, name: edge.node.assignee.name}
                          : undefined,
                  }))
                : [];

        // Check if email entry should be filtered out
        if (onFilterOutEvent && !shouldEmailAppearOnLeadTimeline(emailRelatedLeads, entityPageId)) {
            onFilterOutEvent();
        }

        const isZendeskTicketMessage = payload.isTicketMessage;

        return (
            <div ref={emailEntryRef}>
                <TimelineEmailProvider
                    suggestedEmailReplies={suggestedEmailReplies}
                    allowSuggestedReplies={allowSuggestedReplies && !isZendeskTicketMessage}
                    isLoadingSuggestedReplies={isLoadingSuggestedReplies}
                    onReply={onReply}
                >
                    <TimelineEntry
                        id={id}
                        icon={TIMELINE_ENTRY_ICONS[getEmailIconVariant(payload)]!}
                        entryType={getEntryTypeFromPayload(payload)}
                        timestamp={changeTime}
                        subject={getSubjectForEmail(payload)}
                        subjectTitle={payload.subject}
                        actor={getActorForEmail(payload.from)}
                        status={getStatusForEmail(payload, onRetryFailedEmail, onUpdateEmail)}
                        participants={participants}
                        relatedLeads={relatedLeads.filter((lead) => lead.isChecked)}
                        content={payload.bodyExcerpt}
                        changeType={changeType}
                        reactions={reactions}
                        // @ts-expect-error
                        comments={comments}
                        isDashboard={isDashboard}
                        onAddComment={(value) =>
                            createComment({
                                changeLogId: id,
                                parentId: payload.id,
                                value,
                                user: props.currentUser,
                            })
                        }
                        onDeleteComment={(commentId) => deleteComment({changeLogId: id, commentId})}
                        currentUser={currentUser}
                        customBorderProps={getCustomBorder(payload)}
                        emailEngagement={{
                            attachments: payload.attachments,
                            clicks: payload.trackedClicksCount || 0,
                            opens: payload.trackedViewsCount || 0,
                        }}
                        disableComments={disableComments}
                        actionMenu={
                            <EmailEventActionMenu
                                changeLogId={id}
                                currentUser={currentUser}
                                email={payload}
                                onDeleteEmail={deleteEmailAndEvent}
                                onUpdateEmail={onUpdateEmail}
                                onRetryFailedEmail={onRetryFailedEmail}
                                isViewingAttachments={isViewingAttachments}
                                setIsViewingAttachments={setIsViewingAttachments}
                                onReply={onReply}
                                onReplyAll={onReplyAll}
                            />
                        }
                        additionalToolbarOptions={getAdditionalToolbarOptionsForEmail(payload, {
                            onReply,
                            onReplyAll,
                            expandable: {
                                isExpanded,
                                setIsExpanded,
                            },
                            relatedLeads:
                                isDashboard || !relatedLeads.length
                                    ? undefined
                                    : {
                                          toolbarButton: (
                                              <EmailRelatedLeadsButton
                                                  relatedLeads={relatedLeads}
                                                  updateEmailRelatedLeadAssociations={(
                                                      leadAssociations
                                                  ) =>
                                                      updateEmailRelatedLeadAssociations({
                                                          emailId: payload.id,
                                                          leadAssociations,
                                                      })
                                                  }
                                              />
                                          ),
                                      },
                        })}
                        onClickOpen={() => {
                            appHistory.push(`#${payload.htmlUrlPath}`);
                        }}
                        isExpanded={isExpanded}
                        hasGutterLine={hasGutterLine}
                    />
                </TimelineEmailProvider>
            </div>
        );
    }

    return null;
}

export function TimelineEntryEmail(props: OwnProps) {
    const emailId = props.event.payload.__typename === 'Email' ? props.event.payload.id : undefined;

    if (emailId) {
        return (
            <EmailMutationWrapper>
                {({updateEmail, retryFailedEmail, deleteEmail}) => (
                    <TimelineEntryEmailComponent
                        {...props}
                        eventEmailId={emailId}
                        onUpdateEmail={(id, shouldShare) => {
                            updateEmail(id, shouldShare);
                        }}
                        onRetryFailedEmail={(id) => {
                            retryFailedEmail(id);
                        }}
                        onDeleteEmail={(id, allByAddress) => {
                            deleteEmail(id, allByAddress);
                        }}
                    />
                )}
            </EmailMutationWrapper>
        );
    }

    return null;
}
